import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import {
  Briefcase, Hand, LogOut, Flag, Activity, RefreshCw, AlertCircle, Send, User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];
const WORK_STATUSES = ['NOT_STARTED', 'CLAIMED', 'IN_PROGRESS', 'WAITING', 'COMPLETED', 'ARCHIVED'];

export default function RecruiterWorkspace() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [noteBody, setNoteBody] = useState('');
  const [busy, setBusy] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchQueue = useCallback(async () => {
    setLoading(true); setError('');
    try {
      // Assigned queue is sourced from the read-only recruiter-queue search endpoint.
      const r = await fetch(`${API_URL}/api/recruiter-queue/search?page=1&page_size=50`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.status === 403) {
        // recruiter-queue may be disabled independently; still allow actions on a selected id
        setRows([]);
      } else if (r.ok) {
        const d = await r.json();
        setRows(d.items || []);
        if (!selected && (d.items || []).length) setSelected(d.items[0]);
      } else {
        throw new Error('Failed to load workspace.');
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const refreshSelected = async (id) => {
    try {
      const r = await fetch(`${API_URL}/api/recruiter-queue/application/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setSelected(await r.json());
    } catch { /* best-effort */ }
  };

  const act = async (path, method = 'POST', body = null) => {
    if (!selected) return;
    setBusy(true); setError('');
    try {
      const opts = { method, headers };
      if (body) opts.body = JSON.stringify(body);
      const r = await fetch(`${API_URL}/api/recruiter-actions/${path}`, opts);
      if (r.status === 403) throw new Error('This feature is currently disabled.');
      if (r.status === 409) throw new Error('Application is already claimed by another recruiter.');
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Action failed.');
      }
      const updated = await r.json();
      setSelected(prev => ({ ...prev, ...updated }));
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const claim = () => act(`claim/${selected.id}`);
  const release = () => act(`release/${selected.id}`);
  const setPriority = (priority) => act('priority', 'POST', { application_id: selected.id, priority });
  const setStatus = (work_status) => act('status', 'POST', { application_id: selected.id, work_status });
  const addNote = async () => {
    if (!noteBody.trim()) return;
    await act('note', 'POST', { application_id: selected.id, body: noteBody.trim() });
    setNoteBody('');
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-6" data-testid="recruiter-workspace">
        <Breadcrumb items={[{ label: 'Recruiter Workspace' }]} />
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-blue-700" /> Recruiter Workspace
            </h1>
            <p className="text-sm text-gray-600">Manage your recruiter work — claim, prioritize, note, and progress applications.</p>
          </div>
          <button onClick={fetchQueue} data-testid="workspace-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-900 bg-blue-50 rounded-lg hover:bg-blue-100">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-6" data-testid="workspace-error">
            <div className="flex items-center gap-2"><AlertCircle className="w-5 h-5" /> {error}</div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Assigned queue */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="assigned-queue">
            <div className="px-4 py-3 border-b border-gray-200"><h3 className="text-sm font-semibold text-gray-900">Assigned Queue</h3></div>
            {loading ? (
              <div className="py-12 flex items-center justify-center text-gray-500" data-testid="workspace-loading"><RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading...</div>
            ) : rows.length === 0 ? (
              <div className="py-12 text-center text-gray-400" data-testid="workspace-empty">No applications available.</div>
            ) : (
              <ul className="divide-y divide-gray-100 max-h-[560px] overflow-y-auto">
                {rows.map(a => (
                  <li key={a.id}>
                    <button onClick={() => { setSelected(a); refreshSelected(a.id); }} data-testid={`workspace-row-${a.id}`}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${selected?.id === a.id ? 'bg-blue-50' : ''}`}>
                      <div className="text-sm font-medium text-gray-900">{a.first_name} {a.last_name}</div>
                      <div className="text-xs text-gray-500">{a.email}</div>
                      <div className="text-xs text-gray-400 mt-1">{a.work_status || 'NOT_STARTED'} · {a.priority || 'NORMAL'}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Detail / actions */}
          <div className="lg:col-span-2 space-y-6">
            {!selected ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">Select an application to manage.</div>
            ) : (
              <>
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{selected.first_name} {selected.last_name}</h3>
                      <p className="text-sm text-gray-500">{selected.email}</p>
                      <p className="text-xs text-gray-500 mt-1 flex items-center gap-1" data-testid="assigned-user">
                        <User className="w-3 h-3" /> Claimed by: {selected.claimed_by || '—'} · Assigned: {selected.assigned_recruiter || '—'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={claim} disabled={busy} data-testid="claim-button"
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                        <Hand className="w-4 h-4" /> Claim
                      </button>
                      <button onClick={release} disabled={busy} data-testid="release-button"
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50">
                        <LogOut className="w-4 h-4" /> Release
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-gray-600 flex items-center gap-1 mb-1"><Flag className="w-3 h-3" /> Priority</label>
                      <select value={selected.priority || 'NORMAL'} onChange={(e) => setPriority(e.target.value)} disabled={busy}
                        data-testid="priority-select" className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white">
                        {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 flex items-center gap-1 mb-1"><Activity className="w-3 h-3" /> Work Status</label>
                      <select value={selected.work_status || 'NOT_STARTED'} onChange={(e) => setStatus(e.target.value)} disabled={busy}
                        data-testid="status-select" className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white">
                        {WORK_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Notes timeline + activity */}
                <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="activity-feed">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Notes & Activity</h3>
                  <div className="flex gap-2 mb-4">
                    <input type="text" value={noteBody} onChange={(e) => setNoteBody(e.target.value)}
                      placeholder="Add a note..." data-testid="note-input"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                    <button onClick={addNote} disabled={busy || !noteBody.trim()} data-testid="note-submit"
                      className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                      <Send className="w-4 h-4" /> Add
                    </button>
                  </div>
                  {(selected.recruiter_notes || []).length === 0 ? (
                    <div className="text-sm text-gray-400 py-4 text-center">No notes yet.</div>
                  ) : (
                    <ul className="space-y-3">
                      {[...(selected.recruiter_notes || [])].reverse().map((n, i) => (
                        <li key={i} className="border-l-2 border-blue-200 pl-3">
                          <div className="text-sm text-gray-900">{n.body}</div>
                          <div className="text-xs text-gray-500">{n.author} · {(n.timestamp || '').slice(0, 16).replace('T', ' ')}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="text-xs text-gray-400 mt-4">Last activity: {(selected.last_activity || '—').slice(0, 16).replace('T', ' ')}</div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
